import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as loginApi, register as registerApi, getCurrentUser } from '../api/endpoints';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Listen for session-expired events fired by the axios interceptor.
  // This lets the interceptor trigger a clean React-based redirect to /login
  // instead of using window.location.href (which causes a white flash).
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const loadUser = async () => {
    try {
      const response = await getCurrentUser();
      setUser(response.data);
      setIsAuthenticated(true);
      return { ok: true };
    } catch (error) {
      console.error('Error loading user:', error);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setIsAuthenticated(false);
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      return { ok: false, reason: detail };
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await loginApi({ email, password });
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      const loaded = await loadUser();
      if (!loaded.ok) {
        return { success: false, error: loaded.reason || 'Could not load your profile. Please try again.' };
      }
      return { success: true };
    } catch (error) {
      // Network error (backend not running) vs API error (bad credentials, etc.)
      const detail = error.response?.data?.detail;
      const fallback = error.message === 'Network Error'
        ? 'Cannot reach the server. Please check your connection.'
        : 'Login failed. Please try again.';
      return {
        success: false,
        error: detail || fallback,
      };
    }
  };

  const register = async (email, password, fullName, additionalFields = {}) => {
    try {
      await registerApi({
        email,
        password,
        full_name: fullName,
        institution: additionalFields.institution,
        current_year: additionalFields.current_year,
        degree_type: additionalFields.degree_type,
        country: additionalFields.country,
        gender: additionalFields.gender,
        marketing_consent: additionalFields.marketing_consent || false
      });
      // Auto-login after registration
      return await login(email, password);
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('sessionStats'); // Clear session stats on logout
    localStorage.removeItem('sessionCategoryStats'); // Clear session category stats on logout
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        register,
        logout,
        loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};